import { useEffect, useState, useCallback, useRef } from 'react';

const DEFAULT_FEEDBACK_DURATION = 2000;

export type UseCopyToClipboardReturn = {
  isCopied: boolean;
  copyToClipboard: (text: string) => Promise<void>;
};

export const useCopyToClipboard = (
  feedbackDuration: number = DEFAULT_FEEDBACK_DURATION,
): UseCopyToClipboardReturn => {
  const [isCopied, setIsCopied] = useState(false);
  const copyTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const copyToClipboard = useCallback(
    async (text: string) => {
      await navigator.clipboard.writeText(text);
      setIsCopied(true);

      if (copyTimeoutRef.current) {
        clearTimeout(copyTimeoutRef.current);
      }

      copyTimeoutRef.current = setTimeout(() => {
        setIsCopied(false);
        copyTimeoutRef.current = null;
      }, feedbackDuration);
    },
    [feedbackDuration],
  );

  useEffect(() => {
    return () => {
      if (copyTimeoutRef.current) {
        clearTimeout(copyTimeoutRef.current);
        copyTimeoutRef.current = null;
      }
    };
  }, []);

  return {
    isCopied,
    copyToClipboard,
  };
};
